export type OrderTypeMode = 'MARKET' | 'LIMIT'

function finiteOrNull(v: number | string | null | undefined): number | null {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * Side-aware marketable limit:
 *   BUY  -> ask (cross the spread up)
 *   SELL -> bid (cross the spread down)
 * Falls back to `last` if the touched side is missing.
 * Returns null when no usable price exists (caller should block submission).
 */
export function resolveLimitPrice(args: {
  side: 'BUY' | 'SELL'
  bid?: number | string | null
  ask?: number | string | null
  last?: number | string | null
}): number | null {
  const bid = finiteOrNull(args.bid)
  const ask = finiteOrNull(args.ask)
  const last = finiteOrNull(args.last)
  const touch = args.side === 'BUY' ? ask : bid
  return touch ?? last
}

export function roundToCent(n: number): number {
  return Math.round(n * 100) / 100
}

/** Slippage ceiling for extended-hours marketable limits (0.2%). */
export const MARKETABLE_BUFFER = 0.002

/**
 * Round to the broker's valid price increment (2 decimals >= $1, 4 below),
 * biased toward marketability: BUY rounds up, SELL rounds down.
 */
export function roundToTick(price: number, side: 'BUY' | 'SELL'): number {
  const decimals = price >= 1 ? 2 : 4
  const tick = price >= 1 ? 0.01 : 0.0001
  const ticks = side === 'BUY'
    ? Math.ceil(price / tick - 1e-7)
    : Math.floor(price / tick + 1e-7)
  return Number((ticks * tick).toFixed(decimals))
}

/**
 * Marketable Limit (see CONTEXT.md): cross the spread plus a small buffer.
 * The buffer is a slippage *ceiling* — a limit order still fills at the best
 * available price. Used for all extended-hours equity orders (brokers reject
 * MARKET outside RTH).
 */
export function resolveMarketableLimitPrice(args: {
  side: 'BUY' | 'SELL'
  bid?: number | string | null
  ask?: number | string | null
  last?: number | string | null
}): number | null {
  const touch = resolveLimitPrice(args)
  if (touch == null) return null
  const buffered =
    args.side === 'BUY' ? touch * (1 + MARKETABLE_BUFFER) : touch * (1 - MARKETABLE_BUFFER)
  return roundToTick(buffered, args.side)
}
